import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, Label, Dialog, DialogContent, DialogHeader, DialogTitle } from '@caruso/ui';
import {
  Plus,
  Smartphone,
  Wifi,
  WifiOff,
  Loader2,
  QrCode,
  Trash2,
  RefreshCw,
  Star,
  MoreVertical,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { whatsappConnectionsService, WhatsAppConnection, CreateConnectionDto } from '../../services/whatsapp-connections.service';

export function WhatsAppConnections() {
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<WhatsAppConnection | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [newConnection, setNewConnection] = useState<CreateConnectionDto>({
    name: '',
    instanceName: '',
  });

  // Query para listar conexões
  const { data: connections, isLoading } = useQuery({
    queryKey: ['whatsapp-connections'],
    queryFn: () => whatsappConnectionsService.getAll(),
  });

  // Mutation para criar conexão
  const createMutation = useMutation({
    mutationFn: (data: CreateConnectionDto) => whatsappConnectionsService.create(data),
    onSuccess: () => {
      toast.success('Conexão criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-connections'] });
      setShowCreateDialog(false);
      setNewConnection({ name: '', instanceName: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao criar conexão');
    },
  });

  // Mutation para deletar conexão
  const deleteMutation = useMutation({
    mutationFn: (id: string) => whatsappConnectionsService.delete(id),
    onSuccess: () => {
      toast.success('Conexão removida');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-connections'] });
    },
    onError: () => {
      toast.error('Erro ao remover conexão');
    },
  });

  // Mutation para conectar (QR Code)
  const connectMutation = useMutation({
    mutationFn: (id: string) => whatsappConnectionsService.connect(id),
    onSuccess: (data) => {
      if (data.qrcode) {
        setQrCode(data.qrcode);
        setShowQrDialog(true);
      } else {
        toast.info('Aguardando QR Code...');
      }
    },
    onError: () => {
      toast.error('Erro ao gerar QR Code');
    },
  });

  // Mutation para desconectar
  const logoutMutation = useMutation({
    mutationFn: (id: string) => whatsappConnectionsService.logout(id),
    onSuccess: () => {
      toast.success('Desconectado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-connections'] });
    },
    onError: () => {
      toast.error('Erro ao desconectar');
    },
  });

  // Mutation para verificar status
  const checkStatusMutation = useMutation({
    mutationFn: (id: string) => whatsappConnectionsService.checkStatus(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-connections'] });
      toast.success(`Status: ${data.status}`);
    },
    onError: () => {
      toast.error('Erro ao verificar status');
    },
  });

  // Mutation para definir como padrão
  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => whatsappConnectionsService.update(id, { isDefault: true }),
    onSuccess: () => {
      toast.success('Conexão definida como padrão');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-connections'] });
    },
    onError: () => {
      toast.error('Erro ao definir como padrão');
    },
  });

  const handleCreate = () => {
    if (!newConnection.name || !newConnection.instanceName) {
      toast.error('Preencha todos os campos');
      return;
    }
    createMutation.mutate(newConnection);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'text-green-400';
      case 'CONNECTING':
        return 'text-yellow-400';
      default:
        return 'text-red-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'OPEN':
        return <Wifi className="h-5 w-5 text-green-400" />;
      case 'CONNECTING':
        return <Loader2 className="h-5 w-5 text-yellow-400 animate-spin" />;
      default:
        return <WifiOff className="h-5 w-5 text-red-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'Conectado';
      case 'CONNECTING':
        return 'Conectando...';
      case 'CLOSE':
        return 'Desconectado';
      default:
        return 'Offline';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Conexões WhatsApp</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie múltiplas contas WhatsApp para sua empresa
          </p>
        </div>
        <Button variant="premium" onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Conexão
        </Button>
      </div>

      {/* Lista de Conexões */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-brand-gold animate-spin" />
        </div>
      ) : connections?.length === 0 ? (
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-12 text-center">
            <Smartphone className="h-12 w-12 text-brand-gold/40 mx-auto mb-4" />
            <h3 className="text-lg font-serif text-brand-gold mb-2">
              Nenhuma conexão configurada
            </h3>
            <p className="text-brand-ivory/60 mb-6">
              Crie sua primeira conexão WhatsApp para começar a enviar mensagens
            </p>
            <Button variant="premium" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Criar Primeira Conexão
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {connections?.map((connection, index) => (
            <motion.div
              key={connection.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        {connection.profilePicUrl ? (
                          <img
                            src={connection.profilePicUrl}
                            alt={connection.name}
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-brand-gold/10 flex items-center justify-center">
                            <Smartphone className="h-6 w-6 text-brand-gold" />
                          </div>
                        )}
                        <div className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-brand-ink ${
                          connection.status === 'OPEN' ? 'bg-green-400' :
                          connection.status === 'CONNECTING' ? 'bg-yellow-400' : 'bg-red-400'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-brand-ivory">{connection.name}</h3>
                          {connection.isDefault && (
                            <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                          )}
                        </div>
                        <p className="text-sm text-brand-ivory/50">
                          {connection.phoneNumber || connection.instanceName}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2 mb-4 py-2 px-3 rounded-lg bg-brand-smoke/30">
                    {getStatusIcon(connection.status)}
                    <span className={`text-sm font-medium ${getStatusColor(connection.status)}`}>
                      {getStatusText(connection.status)}
                    </span>
                    {connection.profileName && (
                      <span className="text-sm text-brand-ivory/40 ml-auto">
                        {connection.profileName}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {connection.status === 'OPEN' ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => checkStatusMutation.mutate(connection.id)}
                          disabled={checkStatusMutation.isPending}
                        >
                          <RefreshCw className={`h-4 w-4 mr-1 ${checkStatusMutation.isPending ? 'animate-spin' : ''}`} />
                          Atualizar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => logoutMutation.mutate(connection.id)}
                          disabled={logoutMutation.isPending}
                        >
                          <LogOut className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="premium"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setSelectedConnection(connection);
                            connectMutation.mutate(connection.id);
                          }}
                          disabled={connectMutation.isPending}
                        >
                          <QrCode className="h-4 w-4 mr-1" />
                          Conectar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => checkStatusMutation.mutate(connection.id)}
                          disabled={checkStatusMutation.isPending}
                        >
                          <RefreshCw className={`h-4 w-4 ${checkStatusMutation.isPending ? 'animate-spin' : ''}`} />
                        </Button>
                      </>
                    )}
                    {!connection.isDefault && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDefaultMutation.mutate(connection.id)}
                        title="Definir como padrão"
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm('Tem certeza que deseja remover esta conexão?')) {
                          deleteMutation.mutate(connection.id);
                        }
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Dialog de Criar Conexão */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-brand-ink border-brand-gold/20">
          <DialogHeader>
            <DialogTitle className="text-brand-gold">Nova Conexão WhatsApp</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Conexão</Label>
              <Input
                id="name"
                placeholder="Ex: Vendas, Suporte, Marketing..."
                value={newConnection.name}
                onChange={(e) => setNewConnection({ ...newConnection, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instanceName">Nome da Instância (Evolution API)</Label>
              <Input
                id="instanceName"
                placeholder="Ex: vendas-caruso (sem espaços)"
                value={newConnection.instanceName}
                onChange={(e) => setNewConnection({
                  ...newConnection,
                  instanceName: e.target.value.toLowerCase().replace(/\s/g, '-')
                })}
              />
              <p className="text-xs text-brand-ivory/40">
                Identificador único no Evolution API. Use apenas letras, números e hífens.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Criar Conexão
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de QR Code */}
      <Dialog open={showQrDialog} onOpenChange={setShowQrDialog}>
        <DialogContent className="bg-brand-ink border-brand-gold/20">
          <DialogHeader>
            <DialogTitle className="text-brand-gold">
              Conectar {selectedConnection?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 flex flex-col items-center">
            {qrCode ? (
              <>
                <div className="bg-white p-4 rounded-lg mb-4">
                  <img src={qrCode} alt="QR Code" className="w-64 h-64" />
                </div>
                <p className="text-brand-ivory/60 text-center text-sm">
                  Abra o WhatsApp no seu celular, vá em <strong>Configurações {'>'} Aparelhos conectados</strong> e escaneie o QR Code acima.
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    if (selectedConnection) {
                      connectMutation.mutate(selectedConnection.id);
                    }
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Gerar novo QR Code
                </Button>
              </>
            ) : (
              <div className="flex flex-col items-center py-8">
                <Loader2 className="h-8 w-8 text-brand-gold animate-spin mb-4" />
                <p className="text-brand-ivory/60">Gerando QR Code...</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
